import React, { useCallback, useEffect, useState } from 'react'
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Typography,
    makeStyles,
    Theme,
} from '@material-ui/core'
import ExtensionIcon from '@material-ui/icons/Extension'
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline'
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline'
import HourglassEmptyIcon from '@material-ui/icons/HourglassEmpty'
import LinkOffIcon from '@material-ui/icons/LinkOff'

import { useAppDispatch, useUser } from '../../hooks'
import { StripeAccountStatus, useRent } from '../../hooks/useRent'
import { StripeConnectAccount } from '../../models/Rent/types'
import { setWorkSpaceUser } from '../../store'
import StripeLogo from '../../assets/Stripe_Logo_0.svg'

const useStyles = makeStyles((theme: Theme) => ({
    root: {
        maxWidth: 720,
        margin: '0 auto',
        padding: theme.spacing(3),
    },
    header: {
        marginBottom: theme.spacing(3),
    },
    titleRow: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1.5),
    },
    titleIcon: {
        width: 36,
        height: 36,
        borderRadius: theme.spacing(1),
        backgroundColor: theme.palette.primary.main + '14',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        color: theme.palette.primary.main,
        flexShrink: 0,
    },
    subtitle: {
        color: theme.palette.text.secondary,
        fontSize: '0.875rem',
        marginTop: theme.spacing(0.25),
    },
    cards: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(2),
    },
    card: {
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: theme.spacing(1),
        padding: theme.spacing(2.5),
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(2),
    },
    cardHeader: {
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: theme.spacing(2),
    },
    cardBrand: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1.5),
        minWidth: 0,
    },
    brandMark: {
        width: 44,
        height: 44,
        borderRadius: theme.spacing(1),
        backgroundColor: theme.palette.grey[50],
        border: `1px solid ${theme.palette.divider}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        overflow: 'hidden',
    },
    brandLogo: {
        height: 22,
        width: 'auto',
        maxWidth: 36,
    },
    checkrMark: {
        fontWeight: 700,
        fontSize: '0.95rem',
        letterSpacing: '-0.02em',
        color: theme.palette.text.primary,
    },
    cardTitle: {
        fontWeight: 600,
        fontSize: '1.05rem',
        lineHeight: 1.3,
    },
    cardDescription: {
        color: theme.palette.text.secondary,
        fontSize: '0.875rem',
        marginTop: theme.spacing(0.25),
    },
    cardBody: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(1.5),
    },
    statusRow: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        flexWrap: 'wrap',
    },
    statusDetail: {
        color: theme.palette.text.secondary,
        fontSize: '0.875rem',
    },
    actions: {
        display: 'flex',
        gap: theme.spacing(1),
        flexWrap: 'wrap',
    },
    loadingWrap: {
        display: 'flex',
        justifyContent: 'center',
        padding: theme.spacing(6, 0),
    },
}))

type StatusTone = 'success' | 'warning' | 'error' | 'default' | 'info'

function stripeStatusMeta(
    account: StripeConnectAccount | null | undefined,
): { label: string; tone: StatusTone; detail: string } {
    if (!account) {
        return {
            label: 'Not connected',
            tone: 'default',
            detail:
                'Connect Stripe to accept rent, application fees, and other property payments.',
        }
    }

    const status = account.status as StripeAccountStatus
    switch (status) {
        case 'enabled':
            return {
                label: 'Connected',
                tone: 'success',
                detail: 'Charges and payouts are enabled for this property.',
            }
        case 'in_review':
            return {
                label: 'Under review',
                tone: 'info',
                detail:
                    'Stripe is reviewing this account. This usually takes 1–2 business days.',
            }
        case 'rejected':
            return {
                label: 'Rejected',
                tone: 'error',
                detail:
                    'Stripe rejected this account. Resume onboarding or contact Stripe support.',
            }
        case 'restricted':
        case 'restricted_soon':
        default:
            return {
                label: 'Setup incomplete',
                tone: 'warning',
                detail:
                    'Finish Stripe onboarding to enable charges and payouts for this property.',
            }
    }
}

function StatusChip({ label, tone }: { label: string; tone: StatusTone }) {
    const color =
        tone === 'success'
            ? 'primary'
            : tone === 'error'
            ? 'secondary'
            : 'default'

    const icon =
        tone === 'success' ? (
            <CheckCircleOutlineIcon />
        ) : tone === 'error' ? (
            <ErrorOutlineIcon />
        ) : tone === 'info' || tone === 'warning' ? (
            <HourglassEmptyIcon />
        ) : (
            <LinkOffIcon />
        )

    return (
        <Chip
            size="small"
            color={color}
            variant={tone === 'default' ? 'outlined' : 'default'}
            icon={icon}
            label={label}
        />
    )
}

/**
 * Property-level third-party account connections for EzManage.
 * Reachable from the user menu. Org-level Integrations deferred.
 */
export const EzManageIntegrationsPage = () => {
    const classes = useStyles()
    const dispatch = useAppDispatch()
    const { workspaceUser } = useUser()
    const {
        fetchStripeConnectAccount,
        CreateStripeConnectAccount,
        createStripeLoginLink,
    } = useRent()

    const [stripeAccount, setStripeAccount] = useState<
        StripeConnectAccount | null | undefined
    >(undefined)
    const [stripeActionLoading, setStripeActionLoading] = useState(false)
    const [stripeError, setStripeError] = useState<string | null>(null)

    const syncStripe = useCallback(async () => {
        if (!workspaceUser) return

        setStripeAccount(undefined)
        setStripeError(null)

        try {
            const account = await fetchStripeConnectAccount()
            setStripeAccount(account)
            dispatch(
                setWorkSpaceUser({
                    ...workspaceUser,
                    active_workspace: {
                        ...workspaceUser.active_workspace,
                        stripe_connect_account: account,
                    },
                }),
            )
        } catch {
            setStripeAccount(
                workspaceUser.active_workspace.stripe_connect_account ?? null,
            )
            setStripeError(
                'Could not refresh Stripe status. Showing cached data.',
            )
        }
    }, [workspaceUser, dispatch, fetchStripeConnectAccount])

    useEffect(() => {
        syncStripe()
    }, [workspaceUser?.id])

    const handleConnectStripe = async () => {
        setStripeActionLoading(true)
        setStripeError(null)
        try {
            await CreateStripeConnectAccount()
        } catch {
            setStripeError('Unable to start Stripe onboarding. Try again.')
            setStripeActionLoading(false)
        }
    }

    const handleOpenStripeDashboard = async () => {
        setStripeActionLoading(true)
        setStripeError(null)
        try {
            const url = await createStripeLoginLink()
            if (url) {
                window.open(url, '_blank', 'noopener,noreferrer')
            } else {
                setStripeError('Stripe dashboard link was not returned.')
            }
        } catch {
            setStripeError('Unable to open Stripe dashboard. Try again.')
        } finally {
            setStripeActionLoading(false)
        }
    }

    const stripeMeta = stripeStatusMeta(stripeAccount)
    const stripeEnabled = stripeAccount?.status === 'enabled'
    const stripeNeedsSetup =
        !stripeAccount ||
        stripeAccount.status === 'restricted' ||
        stripeAccount.status === 'restricted_soon' ||
        stripeAccount.status === 'rejected'

    return (
        <Box className={classes.root}>
            <Box className={classes.header}>
                <Box className={classes.titleRow}>
                    <Box className={classes.titleIcon}>
                        <ExtensionIcon fontSize="small" />
                    </Box>
                    <Box>
                        <Typography variant="h5" component="h1">
                            Integrations
                        </Typography>
                        <Typography className={classes.subtitle}>
                            Property-level account connections for payments and
                            screening
                        </Typography>
                    </Box>
                </Box>
            </Box>

            <Box className={classes.cards}>
                <Box className={classes.card}>
                    <Box className={classes.cardHeader}>
                        <Box className={classes.cardBrand}>
                            <Box className={classes.brandMark}>
                                <img
                                    src={StripeLogo}
                                    alt=""
                                    className={classes.brandLogo}
                                />
                            </Box>
                            <Box>
                                <Typography className={classes.cardTitle}>
                                    Stripe
                                </Typography>
                                <Typography className={classes.cardDescription}>
                                    Accept rent, application fees, and other
                                    payments for this property.
                                </Typography>
                            </Box>
                        </Box>
                        {stripeAccount !== undefined && (
                            <StatusChip
                                label={stripeMeta.label}
                                tone={stripeMeta.tone}
                            />
                        )}
                    </Box>

                    <Box className={classes.cardBody}>
                        {stripeAccount === undefined ? (
                            <Box className={classes.loadingWrap}>
                                <CircularProgress size={28} />
                            </Box>
                        ) : (
                            <>
                                <Box className={classes.statusRow}>
                                    <Typography
                                        className={classes.statusDetail}
                                    >
                                        {stripeMeta.detail}
                                    </Typography>
                                </Box>
                                {stripeError && (
                                    <Typography color="error" variant="body2">
                                        {stripeError}
                                    </Typography>
                                )}
                                <Box className={classes.actions}>
                                    {stripeNeedsSetup && (
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            onClick={handleConnectStripe}
                                            disabled={stripeActionLoading}
                                        >
                                            {stripeActionLoading ? (
                                                <CircularProgress size={22} />
                                            ) : stripeAccount ? (
                                                'Continue Stripe setup'
                                            ) : (
                                                'Connect Stripe'
                                            )}
                                        </Button>
                                    )}
                                    {stripeEnabled && (
                                        <Button
                                            variant="outlined"
                                            color="primary"
                                            onClick={handleOpenStripeDashboard}
                                            disabled={stripeActionLoading}
                                        >
                                            {stripeActionLoading ? (
                                                <CircularProgress size={22} />
                                            ) : (
                                                'Open Stripe dashboard'
                                            )}
                                        </Button>
                                    )}
                                    {stripeAccount?.status === 'in_review' && (
                                        <Button
                                            variant="outlined"
                                            onClick={syncStripe}
                                            disabled={stripeActionLoading}
                                        >
                                            Refresh status
                                        </Button>
                                    )}
                                </Box>
                            </>
                        )}
                    </Box>
                </Box>

                <Box className={classes.card}>
                    <Box className={classes.cardHeader}>
                        <Box className={classes.cardBrand}>
                            <Box className={classes.brandMark}>
                                <Typography className={classes.checkrMark}>
                                    C
                                </Typography>
                            </Box>
                            <Box>
                                <Typography className={classes.cardTitle}>
                                    Checkr
                                </Typography>
                                <Typography className={classes.cardDescription}>
                                    Tenant screening for credit, background, and
                                    related reports on this property.
                                </Typography>
                            </Box>
                        </Box>
                        <StatusChip label="Not connected" tone="default" />
                    </Box>

                    <Box className={classes.cardBody}>
                        <Typography className={classes.statusDetail}>
                            Connect a Checkr account so screening invites and
                            report costs bill to this property. Partner
                            onboarding (same pattern as Stripe) is next —
                            staging currently uses the EZOS Checkr test account.
                        </Typography>
                        <Box className={classes.actions}>
                            <Button
                                variant="contained"
                                color="primary"
                                disabled
                            >
                                Connect Checkr
                            </Button>
                        </Box>
                    </Box>
                </Box>
            </Box>
        </Box>
    )
}
